import { useEffect, useRef } from 'react';
import { shouldRefreshToken, refreshAccessToken } from '../api/tokenRefreshInterceptor';
import client from '../api/client';
import Logger from '../utils/logger';
import storage from '../utils/storage';

/**
 * Hook pour gérer le refresh automatique du token JWT
 * Rafraîchit le token proactivement avant expiration
 * 
 * @param {number} checkIntervalMinutes - Intervalle de vérification en minutes (défaut: 10)
 * @param {number} minutesBeforeExpiry - Rafraîchir X minutes avant expiration (défaut: 5)
 */
export const useTokenRefresh = (checkIntervalMinutes = 10, minutesBeforeExpiry = 5) => {
  const intervalRef = useRef(null);

  useEffect(() => {
    // Démarrer l'intervalle de vérification
    intervalRef.current = setInterval(async () => {
      try {
        if (shouldRefreshToken(minutesBeforeExpiry)) {
          Logger.log('[useTokenRefresh] Rafraîchissement proactif du token...');
          await refreshAccessToken(client);
          Logger.log('[useTokenRefresh] Token rafraîchi avec succès');
        }
      } catch (error) {
        Logger.error('[useTokenRefresh] Erreur lors du rafraîchissement:', error);
        // L'erreur est gérée par le client Axios
      }
    }, checkIntervalMinutes * 60 * 1000);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkIntervalMinutes, minutesBeforeExpiry]);
};

/**
 * Hook pour détecter l'inactivité et déconnecter l'utilisateur
 * 
 * ✅ Synchronisé entre les onglets via localStorage
 * ✅ Déconnexion après 1h d'inactivité (60 minutes)
 * 
 * @param {number} inactivityMinutes - Durée d'inactivité avant déconnexion (défaut: 60)
 */
export const useInactivityLogout = (inactivityMinutes = 60) => {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const resetInactivityTimer = () => {
    lastActivityRef.current = Date.now();

    // ✅ Enregistrer l'activité dans le localStorage pour synchroniser les autres onglets
    storage.recordActivity();

    // Nettoyer le timeout précédent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Définir un nouveau timeout
    timeoutRef.current = setTimeout(() => {
      Logger.warn('[useInactivityLogout] Inactivité détectée, déconnexion...');
      
      // ✅ Utiliser storage.clearAll() qui envoie un signal de déconnexion
      storage.clearAll();
      
      // Redirection via React Router (gérée par SecurityInitializer)
      window.location.href = '/';
    }, inactivityMinutes * 60 * 1000);
  };

  useEffect(() => {
    // Événements à tracker
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Ajouter les listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Initialiser le timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [inactivityMinutes]);
};

/**
 * Hook combiné pour gérer à la fois le refresh et l'inactivité
 * 
 * ✅ Refresh du token toutes les 10 minutes
 * ✅ Rafraîchissement 5 minutes avant expiration
 * ✅ Déconnexion après 1h d'inactivité
 * ✅ Synchronisation multi-onglets
 */
export const useAuthSession = (
  checkIntervalMinutes = 10,
  minutesBeforeExpiry = 5,
  inactivityMinutes = 60  // ✅ Changé de 30 à 60 minutes
) => {
  useTokenRefresh(checkIntervalMinutes, minutesBeforeExpiry);
  useInactivityLogout(inactivityMinutes);
};
